import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCheckCircle, faWallet } from "@fortawesome/free-solid-svg-icons"; // Added wallet and check icon
import { toast } from "react-toastify"; // Added ToastContainer
import "react-toastify/dist/ReactToastify.css";
// Assuming AuthGuard and EmailGuard are defined in ../App and work correctly
import { AuthGuard, EmailGuard } from "../App"; 
import { Oval } from "react-loader-spinner"; 
import { StandardURL } from "../App";

const WithdrawalsLandlordAdminPage = () => {
  const [balance, setBalance] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [transactionCode, setTransactionCode] = useState("");
  const [isFetchingData, setIsFetchingData] = useState(true); // Loader for initial data fetch
  const [isSubmitting, setIsSubmitting] = useState(false); // Loader for submit button
  const [isFetchingBalance, setIsFetchingBalance] = useState(false); // Loader for balance fetch

  const token = localStorage.getItem("token");

  // Fetch properties on component mount
  useEffect(() => {
    const fetchProperties = async () => {
      setIsFetchingData(true);
      if (!token) {
        toast.error("Authentication token not found.");
        setIsFetchingData(false);
        return;
      }
      try {
        const response = await axios.get(
          `${StandardURL}/iNyumba/admin/properties/recordsall`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data && response.data.response) {
          setProperties(response.data.response);
        }
      } catch (error) {
        console.error("Error fetching properties:", error);
        toast.error("Failed to load properties.");
      } finally {
        setIsFetchingData(false);
      }
    };

    fetchProperties();
  }, [token]);

  // Fetch balance when a property is selected
  useEffect(() => {
    const fetchBalance = async () => {
      if (token && selectedProperty) {
        setIsFetchingBalance(true);
        setBalance(null); // Clear previous balance
        try {
          const response = await axios.get(
            `${StandardURL}/iNyumba/balance/owner/${selectedProperty}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.data && response.data.response.length > 0) {
            // Format balance to currency string for display
            const balanceValue = parseFloat(response.data.response[0].balance).toLocaleString('en-US', {
              style: 'currency',
              currency: 'KES', // Assuming KES, change if needed
              minimumFractionDigits: 2,
            });
            setBalance(balanceValue);
          } else {
            setBalance("KES 0.00");
          }
        } catch (error) {
          console.error("Error fetching balance:", error);
          // Check for a specific error message from the API
          const errorMessage = error.response?.data?.response || "Error fetching balance.";
          toast.error(errorMessage);
          setBalance("N/A");
        } finally {
          setIsFetchingBalance(false);
        }
      } else {
        setBalance(null);
      }
    };

    fetchBalance();
  }, [token, selectedProperty]);

  const toggleShowBalance = () => {
    setShowBalance(!showBalance);
  };

  const handlePropertySelection = (e) => {
    const propertyId = e.target.value;
    const property = properties.find((p) => p.property_id === propertyId);

    if (property) {
      setSelectedProperty(property.property_id);
      setOwnerId(property.owner_id);
    } else {
      setSelectedProperty("");
      setOwnerId("");
      setBalance(null);
    }
  };

  const handleManualSettlement = async () => {
    if (!selectedProperty || !ownerId || !amountPaid || !transactionCode) {
      toast.error("All fields are required.");
      return;
    }

    // Basic validation for amount
    const parsedAmount = parseFloat(amountPaid);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Please enter a valid amount.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/admin/manualsettlementaddition`,
        {
          ownerid: ownerId,
          propertyid: selectedProperty,
          amountpaid: parsedAmount,
          transactioncode: transactionCode,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.response === "Manual Settlement added successfully") {
        toast.success(
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-xl" />
            Settlement added successfully.
          </div>
        );
        // Reset form fields
        setAmountPaid("");
        setTransactionCode("");

        // Trigger balance re-fetch by temporarily changing and reverting selectedProperty
        const currentProp = selectedProperty;
        setSelectedProperty("");
        setTimeout(() => setSelectedProperty(currentProp), 0);

      } else if (
        response.data.response.includes(
          "You have insufficient funds in your account. Transaction cost is"
        )
      ) {
        toast.error(response.data.response);
      } else {
         toast.error(response.data.response || "Settlement failed with an unknown error.");
      }
    } catch (error) {
      console.error("Error adding settlement:", error);
      toast.error(
        error.response?.data?.response || "Failed to add settlement. Check network or server logs."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render a full-screen loader while fetching initial properties
  if (isFetchingData) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <Oval color="#4A90E2" secondaryColor="#74B9FF" height={50} width={50} ariaLabel="loading-spinner" />
      </div>
    );
  }

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-xl p-6 md:p-10">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-6 border-b pb-3 flex items-center">
              <FontAwesomeIcon icon={faWallet} className="mr-3 text-blue-600" />
              Manual Landlord Settlement
            </h1>

            {/* Balance Section */}
            <div className="mb-8 p-4 bg-blue-50 border-l-4 border-blue-600 rounded-lg shadow-sm">
              <div className="flex justify-between items-center">
                <span className="text-md text-gray-700 font-medium">Available Balance:</span>
                <div className="flex items-center">
                  <span className="font-extrabold text-2xl text-blue-900 mr-4">
                    {isFetchingBalance ? (
                      <Oval color="#4A90E2" height={20} width={20} secondaryColor="#74B9FF" />
                    ) : showBalance ? (
                      balance || "KES 0.00"
                    ) : (
                      "******"
                    )}
                  </span>
                  <button
                    onClick={toggleShowBalance}
                    className="p-1 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors duration-150 focus:outline-none"
                    aria-label={showBalance ? "Hide balance" : "Show balance"}
                  >
                    <FontAwesomeIcon
                      icon={showBalance ? faEyeSlash : faEye}
                      className="text-blue-600 text-lg"
                    />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Balance reflects funds available for withdrawal from the selected property.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Property Selection */}
                <div>
                  <label htmlFor="property-select" className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Property <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="property-select"
                    onChange={handlePropertySelection}
                    className="w-full border border-gray-300 bg-white rounded-lg shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out"
                    value={selectedProperty}
                  >
                    <option value="" disabled>
                      -- Select Property --
                    </option>
                    {properties.map((property) => (
                      <option
                        key={property.property_id}
                        value={property.property_id}
                        className="text-gray-900"
                      >
                        {`${property.title} (ID: ${property.property_id} - Owner: ${property.owner_id})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Owner ID */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Owner ID</label>
                  <input
                    type="text"
                    value={ownerId}
                    readOnly
                    className="w-full border border-gray-300 rounded-lg p-3 bg-gray-100 text-gray-600 cursor-not-allowed"
                    placeholder="Auto-populated"
                  />
                </div>

                {/* Property ID (Read-only) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Property ID</label>
                  <input
                    type="text"
                    value={selectedProperty}
                    readOnly
                    className="w-full border border-gray-300 rounded-lg p-3 bg-gray-100 text-gray-600 cursor-not-allowed"
                    placeholder="Auto-populated"
                  />
                </div>

                {/* Amount Paid */}
                <div>
                  <label htmlFor="amount-paid" className="block text-sm font-semibold text-gray-700 mb-2">
                    Amount Paid (KES) <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="amount-paid"
                    type="number"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out"
                    placeholder="e.g. 5000.00"
                    min="0.01"
                    step="0.01"
                  />
                </div>

                {/* Transaction Code */}
                <div className="md:col-span-2">
                  <label htmlFor="transaction-code" className="block text-sm font-semibold text-gray-700 mb-2">
                    Transaction Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="transaction-code"
                    type="text"
                    value={transactionCode}
                    onChange={(e) => setTransactionCode(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out"
                    placeholder="Enter M-Pesa or bank transaction code"
                  />
                </div>
            </div>
            
            {/* Submit Button */}
            <div className="mt-8">
              <button
                onClick={handleManualSettlement}
                disabled={isSubmitting || !selectedProperty}
                className={`w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-md text-white transition-colors duration-300 ease-in-out 
                  ${isSubmitting || !selectedProperty
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <Oval color="#FFF" secondaryColor="#EEE" height={20} width={20} className="mr-2" />
                    Processing...
                  </>
                ) : (
                  "Submit Manual Settlement"
                )}
              </button>
            </div>

            <p className="mt-4 text-center text-xs text-gray-500">
              * By submitting, you confirm the payment was made offline to the landlord and the balance is accurate.
            </p>
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default WithdrawalsLandlordAdminPage;